import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Package, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { describeLine } from '../lib/cartLine';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-yellow-500/10 text-amber-600 border-yellow-500/20',
  paid: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  shipped: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  delivered: 'bg-green-500/10 text-green-600 border-green-500/20',
  cancelled: 'bg-red-500/10 text-red-600 border-red-500/20',
};

function OrderCard({ order }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="glass-card rounded-2xl overflow-hidden" data-testid={`order-${order.id}`}>
      <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div>
          <p className="text-ink font-bold">{order.order_number}</p>
          <p className="text-ink/55 text-sm">{new Date(order.created_at).toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-4">
          <span className={`text-xs font-bold px-3 py-1 rounded-full border capitalize ${STATUS_COLORS[order.status] || 'bg-ink/[0.04] text-ink/55'}`}>
            {order.status}
          </span>
          <span className="text-honey-700 font-bold">${order.total?.toFixed(2)}</span>
          <Link to={`/orders/${order.id}`} onClick={e => e.stopPropagation()}
            data-testid={`order-detail-link-${order.id}`}
            className="text-ink/45 hover:text-honey-700 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </Link>
          {expanded ? <ChevronUp className="w-4 h-4 text-ink/55" /> : <ChevronDown className="w-4 h-4 text-ink/55" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-ink/10 p-5 space-y-3">
          {order.items?.map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-ink/[0.04] rounded-lg overflow-hidden flex-shrink-0">
                {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <p className="text-ink text-sm">{item.title}</p>
                {describeLine(item) && <p className="text-ink/55 text-xs">{describeLine(item)}</p>}
                <p className="text-ink/55 text-xs">x{item.quantity} · ${item.price?.toFixed(2)} each</p>
              </div>
              <p className="text-ink font-bold text-sm">${(item.price * item.quantity).toFixed(2)}</p>
            </div>
          ))}
          <div className="border-t border-ink/10 pt-3 text-sm space-y-1">
            <div className="flex justify-between text-ink/60"><span>Subtotal</span><span>${order.subtotal?.toFixed(2)}</span></div>
            <div className="flex justify-between text-ink/60"><span>Tax</span><span>${order.tax?.toFixed(2)}</span></div>
            <div className="flex justify-between text-ink/60"><span>Shipping</span><span>${order.shipping?.toFixed(2)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-${order.discount?.toFixed(2)}</span></div>}
            <div className="flex justify-between text-ink font-bold pt-1"><span>Total</span><span className="text-honey-700">${order.total?.toFixed(2)}</span></div>
          </div>
          {order.shipping_address && (
            <div className="border-t border-ink/10 pt-3">
              <p className="text-ink/55 text-xs uppercase tracking-widest mb-1">Shipping to</p>
              <p className="text-ink/70 text-sm">{order.shipping_address.name}, {order.shipping_address.line1}, {order.shipping_address.city}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrderHistory() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/orders`, { withCredentials: true })
      .then(r => setOrders(r.data.orders))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 max-w-3xl mx-auto px-6 pt-8 pb-20">
        <h1 className="text-3xl font-black text-ink mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          My Orders
        </h1>
        {loading ? (
          <div className="flex justify-center pt-20">
            <div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-ink/30 mx-auto mb-4" />
            <p className="text-ink/55 text-lg">No orders yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, i) => (
              <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <OrderCard order={order} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
